import { useEffect, useRef } from "react";
import { useNavigate } from "react-router";
import { jwtDecode } from "jwt-decode";

import { faro } from "@grafana/faro-react";
import { refreshToken } from "@vega-api/generated/auth-login-v1/auth-login-v1";
import { LocalStorageService } from "@vega-core/service/LocalStorage.service";
import { isFalsy } from "@vega-core/utils/isFalsy";
import { isTruthy } from "@vega-core/utils/isTruthy";

const EXPIRATION_THRESHOLD_SECONDS = 300; // 5 minutes
const POLLING_INTERVAL_MS = 60_000; // 1 minute
const MAX_RETRY_ATTEMPTS = 3;
const INITIAL_RETRY_DELAY_MS = 1_000; // 1 second

function isTokenCloseToExpiry(): boolean {
  const token = LocalStorageService.getToken();

  if (isFalsy(token)) {
    return false;
  }

  try {
    const decodedToken = jwtDecode<{ exp: number }>(token);
    const unixTimestamp = Date.now() / 1000;

    return decodedToken.exp - unixTimestamp < EXPIRATION_THRESHOLD_SECONDS;
  } catch (error) {
    console.error("Failed to decode token:", error);

    return true;
  }
}

export function usePollTokenExpiration({ logoutPath }: { logoutPath: string }) {
  const navigate = useNavigate();
  const isRefreshingRef = useRef(false);
  const retryCountRef = useRef(0);
  const oauthProvider = LocalStorageService.getOauthProvider();

  useEffect(() => {
    if (!oauthProvider) {
      return;
    }

    const attemptRefreshWithRetry = async (
      attemptNumber = 0,
    ): Promise<boolean> => {
      try {
        const storedRefreshToken = LocalStorageService.getRefreshToken();

        if (isFalsy(storedRefreshToken)) {
          console.error("No refresh token found, logging out");
          navigate(logoutPath);

          return false;
        }

        const oauthProvider = LocalStorageService.getOauthProvider();

        const { data } = await refreshToken({
          refreshToken: storedRefreshToken,
          oauthProvider,
        });

        if (isTruthy(data?.accessToken)) {
          LocalStorageService.setToken(data.accessToken);
          console.log("Token refreshed successfully via polling");
          retryCountRef.current = 0; // Reset retry count on success

          return true;
        } else {
          console.error("No access token returned during refresh, logging out");
          navigate(logoutPath);

          return false;
        }
      } catch (error) {
        const isLastAttempt = attemptNumber >= MAX_RETRY_ATTEMPTS - 1;

        if (isLastAttempt) {
          faro.api.pushError(error as Error, {
            type: "token_refresh_failed",
            context: {
              attemptNumber: String(attemptNumber),
              maxAttempts: String(MAX_RETRY_ATTEMPTS),
            },
          });

          navigate(logoutPath);

          return false;
        }

        // Exponential backoff: 1s, 2s, 4s
        const delayMs = INITIAL_RETRY_DELAY_MS * Math.pow(2, attemptNumber);
        console.warn(
          `Token refresh attempt ${attemptNumber + 1} failed, retrying in ${delayMs}ms...`,
          error,
        );

        await new Promise((resolve) => setTimeout(resolve, delayMs));

        return attemptRefreshWithRetry(attemptNumber + 1);
      }
    };

    const handleTokenExpireSoon = async () => {
      if (isTokenCloseToExpiry() && !isRefreshingRef.current) {
        isRefreshingRef.current = true;

        await attemptRefreshWithRetry(0);

        isRefreshingRef.current = false;
      }
    };

    handleTokenExpireSoon();

    const pollingIntervalId = window.setInterval(
      handleTokenExpireSoon,
      POLLING_INTERVAL_MS,
    );

    return () => {
      window.clearInterval(pollingIntervalId);
    };
  }, [logoutPath, navigate, oauthProvider]);
}
